import { useEffect, useState } from "react"
import * as React from "react"
import { PaginationType, ZoneConnectionType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useReactToPrint } from "react-to-print"

import { canViewThisPage } from "@/lib/api/auth"
import { getZoneConnectionRateTable } from "@/lib/api/billing"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/data-table"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import { connectionRates } from "@/components/table/columns/zone-connection-rates-columns"
import { ExportOperationModal } from "@/components/table/operations/export-operations"

import AddZoneConnectionRatesPage from "./add"

export const Route = createFileRoute(
  "/_home/zone-settings/zone-connection-rates/",
)({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_clientzoneconnectionrate")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: ConnectionRatesPage,
})

function ConnectionRatesPage() {
  const [zones, setZones] = useState<number[]>([])
  const [connectionTypes, setConnectionTypes] = useState<number[]>([])
  const [defaultPage, setDefaultPage] = useState<number>(10)
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string>("")
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = useState<string | null>(null)

  const {
    data: zonesRateDetails,
    isError,
    isLoading,
  } = useQuery<PaginationType<ZoneConnectionType>>({
    queryKey: [
      "connection-rates",
      defaultPage,
      page,
      search,
      zones,
      connectionTypes,
    ],
    queryFn: () =>
      getZoneConnectionRateTable(
        defaultPage,
        page,
        search,
        zones,
        connectionTypes,
      ),
  })

  useEffect(() => {
    if (zonesRateDetails) {
      setNextPageUrl(zonesRateDetails.next)
      setPrevPageUrl(zonesRateDetails.previous)
    }
  }, [zonesRateDetails])

  const contentRef = React.useRef<HTMLDivElement>(null)
  const reactToPrintFn = useReactToPrint({ contentRef })
  return (
    <main className="space-y-4 p-4">
      <Header>Zone Connection Rates</Header>
      <div
        ref={contentRef}
        className="grid grid-cols-1 md:items-stretch print:p-2"
      >
        <DataTable
          columns={connectionRates}
          data={zonesRateDetails?.results ?? []}
          search={{
            //field: "zone",
            label: "zone",
            value: search,
            onChange: setSearch,
          }}
          //tableTitle="Zone connection rates"
          isLoading={isLoading}
          isError={isError}
          defaultPage={defaultPage}
          setDefaultPage={setDefaultPage}
          nextPageUrl={nextPageUrl}
          prevPageUrl={prevPageUrl}
          page={page}
          setPage={setPage}
          totalCount={zonesRateDetails?.count}
          zones={zones}
          setZones={setZones}
          connectionTypes={connectionTypes}
          setConnectionTypes={setConnectionTypes}
        >
          <div className="flex w-full justify-end">
            <AddZoneConnectionRatesPage />
            <Button className="mx-1" onClick={(e) => reactToPrintFn()}>
              <Icons.download />
              Print Zone Connection Rates
            </Button>
            <ExportOperationModal
              tableData={zonesRateDetails?.results ?? [{}]}
              fileName="Zone Connection Rates"
              showExportModal={showExportModal}
              setShowExportModal={setShowExportModal}
            />
          </div>
        </DataTable>
      </div>
    </main>
  )
}
